import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Link,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DevicesIcon from '@mui/icons-material/Devices';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';

const MigrationModal = ({ open, onClose, actionType }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  console.log('MigrationModal actionType:', actionType);

  const getActionMessage = () => {
    switch (actionType) {
      case 'upload_apk':
        return 'upload APK/IPA files';
      case 'start_emulator':
        return 'start the emulator';
      case 'record_test':
        return 'record mobile tests';
      case 'run_test':
        return 'run a test';
      default:
        return 'perform this action';
    }
  };

  const getActionEmoji = () => {
    switch (actionType) {
      case 'upload_apk':
        return '📤';
      case 'start_emulator':
        return '📱';
      case 'record_test':
        return '🎬';
      case 'run_test':
        return '🚀';
      default:
        return '⚡';
    }
  };

  const features = [
    {
      icon: <CheckCircleIcon sx={{ fontSize: 20 }} />,
      text: 'Seamless migration from Scandium'
    },
    {
      icon: <DevicesIcon sx={{ fontSize: 20 }} />,
      text: 'Mobile testing at mobile.rova.qa'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 20 }} />,
      text: 'Web testing at app.rova.qa'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 20 }} />,
      text: 'Improved performance and reliability'
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          backgroundColor: isDark ? '#1e1e1e' : '#ffffff',
          boxShadow: isDark ? '0 20px 60px rgba(0,0,0,0.6)' : '0 20px 60px rgba(0,0,0,0.15)'
        }
      }}>
      <Box
        sx={{
          height: 4,
          backgroundColor: theme.palette.primary.main
        }}
      />

      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          pt: 2.5
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: isDark ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20
            }}>
            {getActionEmoji()}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Action Required
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
            }
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 2.5
          }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 40,
              boxShadow: isDark
                ? '0 8px 32px rgba(25, 118, 210, 0.3)'
                : '0 8px 32px rgba(25, 118, 210, 0.2)',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: -4,
                borderRadius: '50%',
                padding: 2,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor'
              }
            }}>
            <RocketLaunchIcon sx={{ fontSize: 40, color: '#ffffff' }} />
          </Box>

          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main
              }}>
              Moving to ROVA AI
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
              You're trying to {getActionMessage()}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '90%' }}>
            The Scandium emulator has been discontinued. All testing is now available on{' '}
            <strong style={{ color: theme.palette.primary.main }}>ROVA AI</strong> - our enhanced
            platform for web and mobile testing.
          </Typography>

          {/* Features Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.5,
              width: '100%',
              mt: 1
            }}>
            {features.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                    transform: 'translateY(-2px)',
                    boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)'
                  }
                }}>
                <Box sx={{ color: theme.palette.primary.main }}>{feature.icon}</Box>
                <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'left' }}>
                  {feature.text}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Platform Links */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              width: '100%',
              mt: 1,
              p: 2,
              borderRadius: 2,
              backgroundColor: isDark ? 'rgba(25, 118, 210, 0.06)' : 'rgba(25, 118, 210, 0.03)',
              border: `1px solid ${
                isDark ? 'rgba(25, 118, 210, 0.12)' : 'rgba(25, 118, 210, 0.08)'
              }`
            }}>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Chip
                label="Mobile Testing"
                size="small"
                color="primary"
                sx={{ mb: 0.5, fontWeight: 600 }}
              />
              <Link
                href="https://mobile.rova.qa"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}>
                mobile.rova.qa
                <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
              </Link>
            </Box>
            <Divider orientation={fullScreen ? 'horizontal' : 'vertical'} flexItem />
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Chip
                label="Web Testing"
                size="small"
                color="secondary"
                sx={{ mb: 0.5, fontWeight: 600 }}
              />
              <Link
                href="https://app.rova.qa"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: theme.palette.secondary.main,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}>
                app.rova.qa
                <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
              </Link>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: 'justify-end',
          p: 2.5,
          pt: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`
        }}>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            width: { xs: '100%', sm: 'auto' },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => {
              window.open('https://mobile.rova.qa', '_blank');
              onClose();
            }}
            endIcon={<OpenInNewIcon />}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-2px)',
                boxShadow: isDark
                  ? '0 8px 24px rgba(25, 118, 210, 0.4)'
                  : '0 8px 24px rgba(25, 118, 210, 0.3)'
              },
              transition: 'all 0.2s ease'
            }}>
            Go to ROVA AI
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default MigrationModal;
